import React, { useEffect, useState, useRef } from 'react';
import { Search, Calendar, X } from 'lucide-react';
import { StatusToggle } from './StatusToggle';
import { MultiSelect } from './MultiSelect';
interface FilterBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  selectedManagers: string[];
  onManagersChange: (managers: string[]) => void;
  selectedLoas: string[];
  onLoasChange: (loas: string[]) => void;
  selectedLoanTypes: string[];
  onLoanTypesChange: (types: string[]) => void;
  fundedDateFrom: string;
  fundedDateTo: string;
  onFundedDateFromChange: (date: string) => void;
  onFundedDateToChange: (date: string) => void;
}
const MANAGER_OPTIONS = ['Michael Chen', 'Sarah Williams'];
const LOA_OPTIONS = ['Jennifer Adams', 'David Park', 'Robert Kim'];
const LOAN_TYPE_OPTIONS = ['Conventional', 'FHA', 'VA', 'Jumbo'];
export function FilterBar({
  searchQuery,
  onSearchChange,
  selectedStatus,
  onStatusChange,
  selectedManagers,
  onManagersChange,
  selectedLoas,
  onLoasChange,
  selectedLoanTypes,
  onLoanTypesChange,
  fundedDateFrom,
  fundedDateTo,
  onFundedDateFromChange,
  onFundedDateToChange
}: FilterBarProps) {
  const [showDatePopover, setShowDatePopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowDatePopover(false);
      }
    };
    if (showDatePopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDatePopover]);
  const hasDateRange = fundedDateFrom || fundedDateTo;
  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit'
  }) : '';
  const dateLabel = hasDateRange ? `${formatDate(fundedDateFrom) || 'Any'} – ${formatDate(fundedDateTo) || 'Any'}` : 'Funded Date Range';
  const clearDates = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFundedDateFromChange('');
    onFundedDateToChange('');
  };
  return <div className="bg-white border-b border-slate-200 px-8 py-4" data-id="element-77">
      <div className="flex items-center gap-3 mb-4" data-id="element-78">
        <div className="relative flex-grow max-w-md" data-id="element-79">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-80" />
          <input type="text" placeholder="Search Borrower" value={searchQuery} onChange={e => onSearchChange(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange-500 focus:border-transparent" data-id="element-81" />
        </div>

        <MultiSelect label="All Managers" options={MANAGER_OPTIONS} selected={selectedManagers} onChange={onManagersChange} data-id="element-82" />

        <MultiSelect label="All LOAs" options={LOA_OPTIONS} selected={selectedLoas} onChange={onLoasChange} data-id="element-83" />

        <div className="relative" ref={popoverRef} data-id="element-84">
          <button type="button" onClick={() => setShowDatePopover(v => !v)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-orange-500 ${hasDateRange ? 'border-brand-orange-500 bg-brand-orange-50' : 'border-slate-300'}`} data-id="element-85">
            <Calendar className="w-4 h-4 text-slate-600" data-id="element-86" />
            <span className="text-sm text-slate-700" data-id="element-87">{dateLabel}</span>
            {hasDateRange && <span role="button" tabIndex={0} onClick={clearDates} className="ml-1 p-0.5 rounded hover:bg-slate-200" data-id="element-88">
                <X className="w-3 h-3 text-slate-600" data-id="element-89" />
              </span>}
          </button>

          {showDatePopover && <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-slate-200 rounded-lg shadow-lg z-20 p-4" data-id="element-90">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3" data-id="element-91">
                Funded Date Range
              </div>
              <div className="space-y-3" data-id="element-92">
                <div data-id="element-93">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-94">
                    From
                  </label>
                  <input type="date" value={fundedDateFrom} onChange={e => onFundedDateFromChange(e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange-500" data-id="element-95" />
                </div>
                <div data-id="element-96">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-97">
                    To
                  </label>
                  <input type="date" value={fundedDateTo} onChange={e => onFundedDateToChange(e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange-500" data-id="element-98" />
                </div>
              </div>
              <div className="flex justify-between gap-2 mt-4" data-id="element-99">
                <button type="button" onClick={() => {
              onFundedDateFromChange('');
              onFundedDateToChange('');
            }} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900" data-id="element-100">
                  Clear
                </button>
                <button type="button" onClick={() => setShowDatePopover(false)} className="px-3 py-1.5 bg-brand-orange-500 text-white text-sm font-medium rounded-md hover:bg-brand-orange-600" data-id="element-101">
                  Apply
                </button>
              </div>
            </div>}
        </div>

        <MultiSelect label="All Loan Types" options={LOAN_TYPE_OPTIONS} selected={selectedLoanTypes} onChange={onLoanTypesChange} data-id="element-102" />
      </div>

      <div className="flex items-center justify-between" data-id="element-103">
        <StatusToggle selected={selectedStatus} onChange={onStatusChange} data-id="element-104" />
      </div>
    </div>;
}